import flow from 'lodash/flow';
import scenarioReportStyleBuilder from './styleBuilders/scenarioReportStyleBuilder';
import { SheetExportTypes, Width } from '../xlsxExport.constants';

import {
  buildColumnsMeta,
  buildHeaders,
  buildRows,
  bundleSumAndDetails,
  getGroupingColumns,
  getRowPartials,
  ReportRow,
  TotalColumn,
} from './helpers/scenarioReportHelpers';
import { ReportField } from '@common/constants/scenariosConstants';
import { ScenarioReportDetail } from '@common/schemas/scenarioDetails';
import { ExcelRow } from '@common/xlsxExport/types';

type DisplayedScenarioParams = {
    label: string,
    value: string,
}[];

type AddScenarioParamsParams = {
    displayedScenarioParams: DisplayedScenarioParams,
}
const addScenarioParams = ({ displayedScenarioParams }: AddScenarioParamsParams) =>
  (grid: {
      headerRows: ExcelRow[],
      headerMeta: ExcelRow[],
    }) => ({
    ...grid,
    headerRows: [
      ...displayedScenarioParams.map(({ label, value }) => [label, value]),
      ...grid.headerRows,
    ],
    headerMeta: [
      ...displayedScenarioParams.map(() => ({})),
      ...grid.headerMeta,
    ],
  });

const rescaleColumns = () => (grid: { columnsMeta: {width: number}[] }) => ({
  ...grid,
  columnsMeta: grid.columnsMeta.map(({ width, ...rest }) => ({ width: width * Width.multiplier, ...rest })),
});

export type ScenarioReportBuilderSheet = {
  type: SheetExportTypes.SCENARIO_REPORT,
  displayedReportFields: ReportField[],
  reportsData: Record<string, ReportRow[]>,
  isComparing: boolean,
  detailsData: {
    baseDetailsData: {
      [reportName: string]: {
        [fromField: string]: {
          [countryTaxType: string]: ScenarioReportDetail[]
        }
      }
    },
    compareDetailsData: {
      [reportName: string]: {
        [fromField: string]: {
          [countryTaxType: string]: ScenarioReportDetail[]
        }
      }
    },
  },
  options: {
    hideDecimals: boolean,
    negativeMinus: boolean,
    shouldDisplayZero: boolean,
  },
  displayedScenarioParams: {
    label: string,
    value: string,
  }[],
  totalColumn: TotalColumn,
  name: string,
}
const scenarioReportBuilder = (sheet: ScenarioReportBuilderSheet) => {
  const {
    displayedReportFields,
    reportsData,
    isComparing,
    detailsData,
    options: sheetOptions,
    displayedScenarioParams,
    totalColumn,
    name,
  } = sheet;

  const {
    hideDecimals,
    negativeMinus,
    shouldDisplayZero,
  } = sheetOptions;

  const groupingColumns = getGroupingColumns(reportsData) as { key: string; }[];
  const rowPartials = getRowPartials(isComparing);

  const grid = {
    ...buildRows({
      groupingColumns,
      rowPartials,
      totalColumn,
      data: bundleSumAndDetails({
        isComparing,
        displayedReportFields,
        reportsData,
        detailsData,
      }),
    }),
    ...buildHeaders({ groupingColumns, rowPartials }),
    ...buildColumnsMeta({ groupingColumns, rowPartials }),
  };

  const result = flow([
    addScenarioParams({ displayedScenarioParams }),
    rescaleColumns(),
  ])(grid);

  const { rows, rowsMeta, headerRows, headerMeta, columnsMeta } = result;

  const metadata = scenarioReportStyleBuilder({
    sheetName: name.slice(0, 31),
    numberOfRowsToStick: headerMeta.length,
    numberOfColumnsToStick: isComparing ? 5 : 3,
    rowsMeta: [...headerMeta, ...rowsMeta],
    columnsMeta,
    hideDecimals,
    shouldDisplayZero,
    negativeMinus,
  });

  return {
    metadata,
    grid: [
      ...headerRows,
      ...rows,
    ],
  };
};

export default scenarioReportBuilder;
